import { useCallback, useRef, useState } from 'react'
import { transcribeAudio } from '@/shared/lib/api'

interface UseVoiceTranscriptionOptions {
  onTranscript: (text: string) => void
  onError?: (err: string) => void
}

interface UseVoiceTranscriptionReturn {
  isRecording: boolean
  isTranscribing: boolean
  error: string | null
  startRecording: () => Promise<void>
  stopRecording: () => void
}

function pickMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ]
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) return mime
  }
  return ''
}

export function useVoiceTranscription({
  onTranscript,
  onError,
}: UseVoiceTranscriptionOptions): UseVoiceTranscriptionReturn {
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<BlobPart[]>([])

  const startRecording = useCallback(async () => {
    setError(null)
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Microphone requires a secure connection (HTTPS)')
      }
      // First, get a temporary stream to unlock device enumeration (labels are
      // hidden until permission is granted), then immediately release it.
      const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true })
      tempStream.getTracks().forEach((t) => t.stop())

      // Enumerate devices now that we have permission — pick the current
      // system default so Chrome respects the active output device (e.g. AirPods).
      const devices = await navigator.mediaDevices.enumerateDevices()
      const inputs = devices.filter((d) => d.kind === 'audioinput')
      const defaultDevice = inputs.find((d) => d.deviceId === 'default') ?? inputs[0]

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: defaultDevice ? { deviceId: { exact: defaultDevice.deviceId } } : true,
      })
      streamRef.current = stream

      const track = stream.getAudioTracks()[0]
      console.log('[voice] Recording from:', track?.label)

      const mimeType = pickMimeType()
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      recorderRef.current = recorder
      chunksRef.current = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = async () => {
        streamRef.current?.getTracks().forEach((t) => t.stop())
        streamRef.current = null

        const actualMime = recorder.mimeType || mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type: actualMime })
        chunksRef.current = []

        console.log('[voice] Recorded blob:', blob.size, 'bytes, type:', actualMime)

        if (blob.size === 0) {
          const msg = 'No audio data captured'
          setError(msg)
          onError?.(msg)
          return
        }

        setIsTranscribing(true)
        try {
          const { text } = await transcribeAudio(blob)
          console.log('[voice] Transcription result:', JSON.stringify(text))
          if (text) {
            onTranscript(text)
          } else {
            const msg = 'No speech detected — try speaking louder or longer'
            setError(msg)
            onError?.(msg)
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Transcription failed'
          setError(msg)
          onError?.(msg)
        } finally {
          setIsTranscribing(false)
        }
      }

      recorder.start(250)
      setIsRecording(true)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not access microphone'
      setError(msg)
      onError?.(msg)
    }
  }, [onTranscript, onError])

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop()
    }
    setIsRecording(false)
  }, [])

  return { isRecording, isTranscribing, error, startRecording, stopRecording }
}
